'use client';

import React, { useContext, useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ProjectsContext } from '../contexts/ProjectsContext';
import { getProjectSlug } from '../utils/slugify';
import Loading from './Loading';
import FadeIn from '../components/FadeIn';

function ReplayButton({ onClick, visible = true }) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'flex-end',
      maxWidth: '700px',
      margin: '12px auto 0',
      opacity: visible ? 1 : 0,
      transition: 'opacity 0.4s ease',
      pointerEvents: visible ? 'auto' : 'none',
    }}>
      <button
        onClick={onClick}
        style={{
          background: 'none',
          border: 'none',
          color: '#4a90d9',
          fontSize: '0.8rem',
          fontWeight: 600,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          padding: '4px 0',
          transition: 'color 0.2s ease',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = '#6ab0f9'; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = '#4a90d9'; }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M1 4v6h6" />
          <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
        </svg>
        Replay
      </button>
    </div>
  );
}

function FlowDiagram() {
  const [animKey, setAnimKey] = useState(0);
  const [done, setDone] = useState(false);
  const items = ['Breakdowns', 'Shot Lists', 'Schedules', 'Cast & Crew', 'Call Sheets', 'Calendars'];

  useEffect(() => {
    setDone(false);
    const timer = setTimeout(() => setDone(true), 1.8 * 1000 + items.length * 300 + 400);
    return () => clearTimeout(timer);
  }, [animKey]);

  return (
    <>
      <style>{`
        @keyframes arrow-slide-in {
          0% { opacity: 0; transform: translateX(-8px); }
          100% { opacity: 1; transform: translateX(0); }
        }
        @keyframes arrow-slide-in-reverse {
          0% { opacity: 0; transform: translateX(8px); }
          100% { opacity: 1; transform: translateX(0); }
        }
      `}</style>
      <div
        key={animKey}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          flexWrap: 'wrap',
        }}
      >
        {items.map((name, i) => (
          <React.Fragment key={name}>
            {i > 0 && (
              <span style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '2px',
              }}>
                <span style={{
                  color: '#888888',
                  fontSize: '1.2rem',
                  fontWeight: 300,
                  opacity: 0,
                  animation: 'arrow-slide-in 0.4s ease forwards',
                  animationDelay: `${0.5 + i * 0.3}s`,
                }}>→</span>
                <span style={{
                  color: '#888888',
                  fontSize: '1.2rem',
                  fontWeight: 300,
                  opacity: 0,
                  animation: 'arrow-slide-in-reverse 0.4s ease forwards',
                  animationDelay: `${1.8 + (items.length - 1 - i) * 0.3}s`,
                }}>←</span>
              </span>
            )}
            <span style={{
              padding: '8px 16px',
              backgroundColor: '#111111',
              borderRadius: '8px',
              color: '#cccccc',
              fontSize: '0.85rem',
              fontWeight: 600,
              border: '1px solid #222222',
            }}>
              {name}
            </span>
          </React.Fragment>
        ))}
      </div>
      <ReplayButton onClick={() => setAnimKey(k => k + 1)} visible={done} />
    </>
  );
}

function SyncDemo() {
  const steps = [
    // Start: cursor blinking at end of misspelled name
    { text: 'Samul Rivees', cursor: 12 },
    { text: 'Samul Rivees', cursor: 12 },
    { text: 'Samul Rivees', cursor: 12 },
    { text: 'Samul Rivees', cursor: 12 },
    // Move cursor left to fix "Rivees" → find extra 'e'
    { text: 'Samul Rivees', cursor: 11 },
    { text: 'Samul Rivees', cursor: 10 },
    { text: 'Samul Rivees', cursor: 9 },
    // Pause, delete extra 'e'
    { text: 'Samul Rivees', cursor: 9 },
    { text: 'Samul Rives', cursor: 9 },
    // Type 'r'
    { text: 'Samul Rivers', cursor: 10 },
    // Pause
    { text: 'Samul Rivers', cursor: 10 },
    { text: 'Samul Rivers', cursor: 10 },
    // Now move cursor left to fix "Samul" → missing 'e'
    { text: 'Samul Rivers', cursor: 9 },
    { text: 'Samul Rivers', cursor: 8 },
    { text: 'Samul Rivers', cursor: 7 },
    { text: 'Samul Rivers', cursor: 6 },
    { text: 'Samul Rivers', cursor: 5 },
    { text: 'Samul Rivers', cursor: 4 },
    // Pause, type missing 'e'
    { text: 'Samul Rivers', cursor: 4 },
    { text: 'Samuel Rivers', cursor: 5 },
    // Pause
    { text: 'Samuel Rivers', cursor: 5 },
    { text: 'Samuel Rivers', cursor: 5 },
    // Move cursor back to end
    { text: 'Samuel Rivers', cursor: 6 },
    { text: 'Samuel Rivers', cursor: 7 },
    { text: 'Samuel Rivers', cursor: 8 },
    { text: 'Samuel Rivers', cursor: 9 },
    { text: 'Samuel Rivers', cursor: 10 },
    { text: 'Samuel Rivers', cursor: 11 },
    { text: 'Samuel Rivers', cursor: 12 },
    { text: 'Samuel Rivers', cursor: 13 },
    // Hold final
    { text: 'Samuel Rivers', cursor: 13 },
    { text: 'Samuel Rivers', cursor: 13 },
    { text: 'Samuel Rivers', cursor: 13 },
    { text: 'Samuel Rivers', cursor: 13 },
    { text: 'Samuel Rivers', cursor: 13 },
    { text: 'Samuel Rivers', cursor: 13 },
    { text: 'Samuel Rivers', cursor: 13 },
    { text: 'Samuel Rivers', cursor: 13 },
  ];

  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (stepIndex < steps.length - 1) {
        setStepIndex(stepIndex + 1);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [stepIndex]);

  const { text, cursor } = steps[stepIndex];
  const before = text.slice(0, cursor);
  const after = text.slice(cursor);

  const cards = [
    { app: 'Cast & Crew', context: 'Lead Actor', showCursor: true },
    { app: 'Call Sheets', context: 'Scene 14 — INT. OFFICE', showCursor: false },
    { app: 'Breakdowns', context: 'Scene 14 — Cast', showCursor: false },
    { app: 'Schedules', context: 'Day 3 — Strip #7', showCursor: false },
    { app: 'Shot Lists', context: 'Scene 14 — Shot 3', showCursor: false },
    { app: 'Calendars', context: 'Mar 22 — Shoot Day', showCursor: false },
  ];

  return (
    <>
      <style>{`
        @keyframes cursor-blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
      `}</style>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        maxWidth: '700px',
        margin: '0 auto',
      }}>
        {cards.map((card) => (
          <div key={card.app} style={{
            backgroundColor: '#111111',
            borderRadius: '12px',
            padding: '20px',
            border: '1px solid #1a1a1a',
            textAlign: 'left',
          }}>
            <p style={{
              fontSize: '0.7rem',
              color: '#555555',
              margin: '0 0 4px',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}>
              {card.app}
            </p>
            <p style={{
              fontSize: '0.75rem',
              color: '#666666',
              margin: '0 0 12px',
            }}>
              {card.context}
            </p>
            <p style={{
              fontSize: '1rem',
              color: '#ffffff',
              margin: 0,
              fontWeight: 600,
              fontFamily: 'Helvetica, Arial, sans-serif',
              whiteSpace: 'pre',
            }}>
              {before}
              {card.showCursor && (
                <span style={{
                  display: 'inline-block',
                  width: '2px',
                  height: '1em',
                  backgroundColor: '#2a9d8f',
                  verticalAlign: 'text-bottom',
                  marginLeft: '-1px',
                  marginRight: '-1px',
                  animation: 'cursor-blink 0.8s step-end infinite',
                }} />
              )}
              {after}
            </p>
          </div>
        ))}
      </div>

      <ReplayButton onClick={() => setStepIndex(0)} visible={stepIndex === steps.length - 1} />
    </>
  );
}

function UpdateOnceDemo() {
  const steps = [
    // Pause — show initial state with typos "Mraia Snatso"
    { name: 'Mraia Snatso', role: 'BARISTA', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: -1 },
    { name: 'Mraia Snatso', role: 'BARISTA', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: -1 },
    { name: 'Mraia Snatso', role: 'BARISTA', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: -1 },
    { name: 'Mraia Snatso', role: 'BARISTA', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: -1 },
    // Cursor appears at end
    { name: 'Mraia Snatso', role: 'BARISTA', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 12, field: 'name' },
    { name: 'Mraia Snatso', role: 'BARISTA', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 12, field: 'name' },
    // Move cursor left to fix "Snatso" — go to position 6 (before 'S')
    { name: 'Mraia Snatso', role: 'BARISTA', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 10, field: 'name' },
    { name: 'Mraia Snatso', role: 'BARISTA', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 8, field: 'name' },
    { name: 'Mraia Snatso', role: 'BARISTA', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 6, field: 'name' },
    // Select and delete "Snatso" (6 chars)
    { name: 'Mraia Snatso', role: 'BARISTA', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 6, field: 'name' },
    { name: 'Mraia ', role: 'BARISTA', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 6, field: 'name' },
    // Type "Santos"
    { name: 'Mraia S', role: 'BARISTA', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 7, field: 'name' },
    { name: 'Mraia Sa', role: 'BARISTA', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 8, field: 'name' },
    { name: 'Mraia San', role: 'BARISTA', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 9, field: 'name' },
    { name: 'Mraia Sant', role: 'BARISTA', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 10, field: 'name' },
    { name: 'Mraia Santo', role: 'BARISTA', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 11, field: 'name' },
    { name: 'Mraia Santos', role: 'BARISTA', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 12, field: 'name' },
    // Pause
    { name: 'Mraia Santos', role: 'BARISTA', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 12, field: 'name' },
    { name: 'Mraia Santos', role: 'BARISTA', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 12, field: 'name' },
    // Move cursor left to fix "Mraia" — go to position 0
    { name: 'Mraia Santos', role: 'BARISTA', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 8, field: 'name' },
    { name: 'Mraia Santos', role: 'BARISTA', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 5, field: 'name' },
    { name: 'Mraia Santos', role: 'BARISTA', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 0, field: 'name' },
    // Select and delete "Mraia"
    { name: 'Mraia Santos', role: 'BARISTA', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 0, field: 'name' },
    { name: ' Santos', role: 'BARISTA', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 0, field: 'name' },
    // Type "María"
    { name: 'M Santos', role: 'BARISTA', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 1, field: 'name' },
    { name: 'Ma Santos', role: 'BARISTA', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 2, field: 'name' },
    { name: 'Mar Santos', role: 'BARISTA', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 3, field: 'name' },
    { name: 'Marí Santos', role: 'BARISTA', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 4, field: 'name' },
    { name: 'María Santos', role: 'BARISTA', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 5, field: 'name' },
    // Pause
    { name: 'María Santos', role: 'BARISTA', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 5, field: 'name' },
    // Move to end
    { name: 'María Santos', role: 'BARISTA', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 8, field: 'name' },
    { name: 'María Santos', role: 'BARISTA', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 13, field: 'name' },
    // Pause
    { name: 'María Santos', role: 'BARISTA', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 13, field: 'name' },
    { name: 'María Santos', role: 'BARISTA', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 13, field: 'name' },
    // Now move cursor to role
    { name: 'María Santos', role: 'BARISTA', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 7, field: 'role' },
    { name: 'María Santos', role: 'BARISTA', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 7, field: 'role' },
    // Select all and delete role
    { name: 'María Santos', role: '', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 0, field: 'role' },
    { name: 'María Santos', role: '', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 0, field: 'role' },
    // Type "NEIGHBOR #2"
    { name: 'María Santos', role: 'N', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 1, field: 'role' },
    { name: 'María Santos', role: 'NE', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 2, field: 'role' },
    { name: 'María Santos', role: 'NEI', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 3, field: 'role' },
    { name: 'María Santos', role: 'NEIG', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 4, field: 'role' },
    { name: 'María Santos', role: 'NEIGH', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 5, field: 'role' },
    { name: 'María Santos', role: 'NEIGHB', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 6, field: 'role' },
    { name: 'María Santos', role: 'NEIGHBO', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 7, field: 'role' },
    { name: 'María Santos', role: 'NEIGHBOR', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 8, field: 'role' },
    { name: 'María Santos', role: 'NEIGHBOR ', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 9, field: 'role' },
    { name: 'María Santos', role: 'NEIGHBOR #', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 10, field: 'role' },
    { name: 'María Santos', role: 'NEIGHBOR #2', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 11, field: 'role' },
    // Pause
    { name: 'María Santos', role: 'NEIGHBOR #2', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 11, field: 'role' },
    { name: 'María Santos', role: 'NEIGHBOR #2', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 11, field: 'role' },
    // Move cursor to location — edit "OFFICE" to "COFFEE SHOP"
    { name: 'María Santos', role: 'NEIGHBOR #2', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 11, field: 'loc' },
    { name: 'María Santos', role: 'NEIGHBOR #2', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. OFFICE', cursor: 11, field: 'loc' },
    // Select and delete "OFFICE"
    { name: 'María Santos', role: 'NEIGHBOR #2', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. ', cursor: 5, field: 'loc' },
    { name: 'María Santos', role: 'NEIGHBOR #2', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. ', cursor: 5, field: 'loc' },
    // Type "COFFEE SHOP"
    { name: 'María Santos', role: 'NEIGHBOR #2', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. C', cursor: 6, field: 'loc' },
    { name: 'María Santos', role: 'NEIGHBOR #2', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. CO', cursor: 7, field: 'loc' },
    { name: 'María Santos', role: 'NEIGHBOR #2', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. COF', cursor: 8, field: 'loc' },
    { name: 'María Santos', role: 'NEIGHBOR #2', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. COFF', cursor: 9, field: 'loc' },
    { name: 'María Santos', role: 'NEIGHBOR #2', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. COFFE', cursor: 10, field: 'loc' },
    { name: 'María Santos', role: 'NEIGHBOR #2', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. COFFEE', cursor: 11, field: 'loc' },
    { name: 'María Santos', role: 'NEIGHBOR #2', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. COFFEE ', cursor: 12, field: 'loc' },
    { name: 'María Santos', role: 'NEIGHBOR #2', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. COFFEE S', cursor: 13, field: 'loc' },
    { name: 'María Santos', role: 'NEIGHBOR #2', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. COFFEE SH', cursor: 14, field: 'loc' },
    { name: 'María Santos', role: 'NEIGHBOR #2', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. COFFEE SHO', cursor: 15, field: 'loc' },
    { name: 'María Santos', role: 'NEIGHBOR #2', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. COFFEE SHOP', cursor: 16, field: 'loc' },
    // Pause
    { name: 'María Santos', role: 'NEIGHBOR #2', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. COFFEE SHOP', cursor: 16, field: 'loc' },
    { name: 'María Santos', role: 'NEIGHBOR #2', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. COFFEE SHOP', cursor: 16, field: 'loc' },
    // Move cursor to scenes — position before "14"
    { name: 'María Santos', role: 'NEIGHBOR #2', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. COFFEE SHOP', cursor: 3, field: 'scenes' },
    { name: 'María Santos', role: 'NEIGHBOR #2', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, 14', loc: 'INT. COFFEE SHOP', cursor: 3, field: 'scenes' },
    // Type "B" before 14
    { name: 'María Santos', role: 'NEIGHBOR #2', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, B14', loc: 'INT. COFFEE SHOP', cursor: 4, field: 'scenes' },
    // Hold final
    { name: 'María Santos', role: 'NEIGHBOR #2', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, B14', loc: 'INT. COFFEE SHOP', cursor: 4, field: 'scenes' },
    { name: 'María Santos', role: 'NEIGHBOR #2', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, B14', loc: 'INT. COFFEE SHOP', cursor: -1 },
    { name: 'María Santos', role: 'NEIGHBOR #2', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, B14', loc: 'INT. COFFEE SHOP', cursor: -1 },
    { name: 'María Santos', role: 'NEIGHBOR #2', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, B14', loc: 'INT. COFFEE SHOP', cursor: -1 },
    { name: 'María Santos', role: 'NEIGHBOR #2', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, B14', loc: 'INT. COFFEE SHOP', cursor: -1 },
    { name: 'María Santos', role: 'NEIGHBOR #2', phone: '(310) 555-0147', callTime: '6:00 AM', scenes: '7, B14', loc: 'INT. COFFEE SHOP', cursor: -1 },
  ];

  const [stepIndex, setStepIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const ref = useRef(null);

  // Only start when scrolled into view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasStarted) {
          setIsVisible(true);
          setHasStarted(true);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [hasStarted]);

  useEffect(() => {
    if (!isVisible) return;
    const timer = setTimeout(() => {
      if (stepIndex < steps.length - 1) {
        setStepIndex(stepIndex + 1);
      }
    }, 180);
    return () => clearTimeout(timer);
  }, [stepIndex, isVisible]);

  const step = steps[stepIndex];
  const done = stepIndex === steps.length - 1;

  const renderCursor = (field) => {
    if (step.cursor < 0 || step.field !== field) return null;
    return (
      <span style={{
        display: 'inline-block',
        width: '2px',
        height: '1em',
        backgroundColor: '#2a9d8f',
        verticalAlign: 'text-bottom',
        marginLeft: '-1px',
        marginRight: '-1px',
        animation: 'cursor-blink 0.8s step-end infinite',
      }} />
    );
  };

  const renderField = (value, field) => {
    if (step.field !== field || step.cursor < 0) return value;
    const before = value.slice(0, step.cursor);
    const after = value.slice(step.cursor);
    return <>{before}{renderCursor(field)}{after}</>;
  };

  const labelStyle = {
    fontSize: '0.6rem',
    color: '#444444',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    margin: '0 0 2px',
  };

  const valueStyle = {
    fontSize: '0.85rem',
    color: '#ffffff',
    fontWeight: 600,
    margin: 0,
    whiteSpace: 'pre',
    fontFamily: 'Helvetica, Arial, sans-serif',
    minHeight: '1.2em',
  };

  return (
    <div ref={ref}>
      <style>{`
        @keyframes cursor-blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
      `}</style>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gridAutoRows: '1fr',
        gap: '24px',
        maxWidth: 'none',
        margin: '0 auto',
      }}>
        {/* Cast & Crew card */}
        <div style={{
          minWidth: 0,
          backgroundColor: '#111111',
          borderRadius: '16px',
          border: '1px solid #1a1a1a',
          overflow: 'hidden',
        }}>
          <div style={{
            padding: '10px 16px',
            borderBottom: '1px solid #1a1a1a',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}>
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: '#2a9d8f',
            }} />
            <span style={{
              fontSize: '0.7rem',
              color: '#666666',
              fontWeight: 600,
            }}>
              Cast & Crew Lists
            </span>
          </div>
          <div style={{ padding: '20px' }}>
            {/* Avatar + Name */}
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              marginBottom: '16px',
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: '#1a1a1a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.8rem',
                fontWeight: 700,
                color: '#555555',
                border: '1px solid #222222',
                flexShrink: 0,
              }}>
                MS
              </div>
              <div style={{ textAlign: 'left' }}>
                <p style={{
                  ...valueStyle,
                  fontSize: '1rem',
                  backgroundColor: step.field === 'name' && step.cursor >= 0 ? 'rgba(42, 157, 143, 0.06)' : 'transparent',
                  borderRadius: '4px',
                  padding: '2px 4px',
                  margin: 0,
                  border: step.field === 'name' && step.cursor >= 0 ? '1px solid rgba(42, 157, 143, 0.15)' : '1px solid transparent',
                  transition: 'background-color 0.2s, border-color 0.2s',
                }}>{renderField(step.name, 'name')}</p>
                <p style={{ fontSize: '0.7rem', color: '#555555', margin: '2px 0 0', fontWeight: 500, paddingLeft: '4px' }}>Talent</p>
              </div>
            </div>
            {/* Role */}
            <div style={{ marginBottom: '12px' }}>
              <p style={labelStyle}>Role</p>
              <p style={{
                ...valueStyle,
                backgroundColor: step.field === 'role' && step.cursor >= 0 ? 'rgba(42, 157, 143, 0.06)' : 'transparent',
                borderRadius: '4px',
                padding: '2px 4px',
                margin: '0 -4px',
                border: step.field === 'role' && step.cursor >= 0 ? '1px solid rgba(42, 157, 143, 0.15)' : '1px solid transparent',
                transition: 'background-color 0.2s, border-color 0.2s',
              }}>
                {renderField(step.role, 'role')}
              </p>
            </div>
            {/* Phone */}
            <div>
              <p style={labelStyle}>Phone</p>
              <p style={valueStyle}>{step.phone}</p>
            </div>
          </div>
        </div>

        {/* Call Sheets card */}
        <div style={{
          minWidth: 0,
          backgroundColor: '#111111',
          borderRadius: '16px',
          border: '1px solid #1a1a1a',
          overflow: 'hidden',
        }}>
          <div style={{
            padding: '10px 16px',
            borderBottom: '1px solid #1a1a1a',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}>
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: '#e06c75',
            }} />
            <span style={{
              fontSize: '0.7rem',
              color: '#666666',
              fontWeight: 600,
            }}>
              Production Call Sheets
            </span>
          </div>
          <div style={{ padding: '12px' }}>
            <p style={{
              fontSize: '0.65rem',
              color: '#444444',
              fontWeight: 600,
              margin: '0 0 8px',
              padding: '0 4px',
            }}>
              CALL SHEET — DAY 3
            </p>
            {/* Table */}
            <div style={{
              border: '1px solid #222222',
              borderRadius: '6px',
              overflow: 'hidden',
              fontSize: '0.75rem',
            }}>
              {/* Header row */}
              <div style={{
                display: 'flex',
                backgroundColor: '#1a1a1a',
                borderBottom: '1px solid #222222',
              }}>
                <span style={{ ...labelStyle, width: '30%', margin: 0, padding: '6px 6px', borderRight: '1px solid #222222', overflow: 'hidden' }}>Name</span>
                <span style={{ ...labelStyle, width: '28%', margin: 0, padding: '6px 6px', borderRight: '1px solid #222222', overflow: 'hidden' }}>Role</span>
                <span style={{ ...labelStyle, width: '22%', margin: 0, padding: '6px 6px', textAlign: 'center', borderRight: '1px solid #222222', overflow: 'hidden' }}>Sc</span>
                <span style={{ ...labelStyle, width: '20%', margin: 0, padding: '6px 6px', textAlign: 'right', overflow: 'hidden' }}>Call</span>
              </div>
              {/* Row 1 — James Cole */}
              <div style={{
                display: 'flex',
                backgroundColor: '#111111',
                borderBottom: '1px solid #1a1a1a',
              }}>
                <span style={{ ...valueStyle, width: '30%', fontSize: '0.7rem', color: '#888888', padding: '7px 6px', borderRight: '1px solid #1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis' }}>James Cole</span>
                <span style={{ ...valueStyle, width: '28%', fontSize: '0.7rem', color: '#666666', padding: '7px 6px', borderRight: '1px solid #1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis' }}>DETECTIVE</span>
                <span style={{ ...valueStyle, width: '22%', fontSize: '0.7rem', color: '#666666', padding: '7px 6px', textAlign: 'center', borderRight: '1px solid #1a1a1a', overflow: 'hidden' }}>{step.scenes.includes('B14') ? '1, B14' : '1, 14'}</span>
                <span style={{ ...valueStyle, width: '20%', fontSize: '0.7rem', color: '#666666', padding: '7px 6px', textAlign: 'right', overflow: 'hidden' }}>6:00 AM</span>
              </div>
              {/* Row 2 — María Santos (synced, highlighted) */}
              <div style={{
                display: 'flex',
                backgroundColor: 'rgba(42, 157, 143, 0.05)',
                borderBottom: '1px solid #1a1a1a',
              }}>
                <span style={{ ...valueStyle, width: '30%', fontSize: '0.7rem', padding: '7px 6px', borderRight: '1px solid #1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis' }}>{renderField(step.name, 'name')}</span>
                <span style={{ ...valueStyle, width: '28%', fontSize: '0.7rem', padding: '7px 6px', borderRight: '1px solid #1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis' }}>{renderField(step.role, 'role')}</span>
                <span style={{ ...valueStyle, width: '22%', fontSize: '0.7rem', padding: '7px 6px', textAlign: 'center', borderRight: '1px solid #1a1a1a', overflow: 'hidden' }}>{renderField(step.scenes, 'scenes')}</span>
                <span style={{ ...valueStyle, width: '20%', fontSize: '0.7rem', padding: '7px 6px', textAlign: 'right', overflow: 'hidden' }}>{renderField(step.callTime, 'callTime')}</span>
              </div>
              {/* Row 3 — Ava Chen */}
              <div style={{
                display: 'flex',
                backgroundColor: '#111111',
              }}>
                <span style={{ ...valueStyle, width: '30%', fontSize: '0.7rem', color: '#888888', padding: '7px 6px', borderRight: '1px solid #1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis' }}>Ava Chen</span>
                <span style={{ ...valueStyle, width: '28%', fontSize: '0.7rem', color: '#666666', padding: '7px 6px', borderRight: '1px solid #1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis' }}>LANDLORD</span>
                <span style={{ ...valueStyle, width: '22%', fontSize: '0.7rem', color: '#666666', padding: '7px 6px', textAlign: 'center', borderRight: '1px solid #1a1a1a', overflow: 'hidden' }}>{step.scenes.includes('B14') ? 'B14' : '14'}</span>
                <span style={{ ...valueStyle, width: '20%', fontSize: '0.7rem', color: '#666666', padding: '7px 6px', textAlign: 'right', overflow: 'hidden' }}>8:00 AM</span>
              </div>
            </div>
          </div>
        </div>

        {/* Breakdowns card */}
        <div style={{
          minWidth: 0,
          backgroundColor: '#111111',
          borderRadius: '16px',
          border: '1px solid #1a1a1a',
          overflow: 'hidden',
        }}>
          <div style={{
            padding: '10px 16px',
            borderBottom: '1px solid #1a1a1a',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}>
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: '#e5c07b',
            }} />
            <span style={{
              fontSize: '0.7rem',
              color: '#666666',
              fontWeight: 600,
            }}>
              Script Breakdowns
            </span>
          </div>
          <div style={{ padding: '20px' }}>
            <p style={{
              fontSize: '0.65rem',
              color: '#444444',
              fontWeight: 600,
              margin: '0 0 4px',
            }}>
              SCENE {step.scenes.includes('B14') ? 'B14' : '14'}
            </p>
            <p style={{
              fontSize: '0.8rem',
              color: '#888888',
              fontWeight: 700,
              margin: '0 0 16px',
            }}>
              {step.loc} — DAY
            </p>

            {/* Breakdown categories */}
            <div style={{ marginBottom: '12px' }}>
              <p style={{
                fontSize: '0.6rem',
                color: '#e5c07b',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                margin: '0 0 6px',
                opacity: 0.7,
              }}>
                Cast
              </p>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '4px 6px',
                  margin: '0 -6px',
                  backgroundColor: 'rgba(42, 157, 143, 0.04)',
                  borderRadius: '4px',
                  border: '1px solid rgba(42, 157, 143, 0.1)',
                  transition: 'background-color 0.3s',
                }}>
                  <span style={{ ...valueStyle, fontSize: '0.8rem' }}>{renderField(step.name, 'name')}</span>
                  <span style={{ ...valueStyle, fontSize: '0.8rem', color: '#555555' }}>{renderField(step.role, 'role')}</span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '4px 6px',
                  margin: '0 -6px',
                }}>
                  <span style={{ ...valueStyle, fontSize: '0.8rem', color: '#666666' }}>James Cole</span>
                  <span style={{ ...valueStyle, fontSize: '0.8rem', color: '#555555' }}>DETECTIVE</span>
                </div>
              </div>
            </div>

            <div>
              <p style={{
                fontSize: '0.6rem',
                color: '#e5c07b',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                margin: '0 0 6px',
                opacity: 0.7,
              }}>
                Props
              </p>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
              }}>
                <span style={{ ...valueStyle, fontSize: '0.8rem', color: '#666666', padding: '0 6px' }}>Laptop</span>
                <span style={{ ...valueStyle, fontSize: '0.8rem', color: '#666666', padding: '0 6px' }}>Coffee mug</span>
              </div>
            </div>
          </div>
        </div>
        {/* Shot Lists card */}
        <div style={{
          minWidth: 0,
          backgroundColor: '#111111',
          borderRadius: '16px',
          border: '1px solid #1a1a1a',
          overflow: 'hidden',
        }}>
          <div style={{
            padding: '10px 16px',
            borderBottom: '1px solid #1a1a1a',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}>
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: '#61afef',
            }} />
            <span style={{
              fontSize: '0.7rem',
              color: '#666666',
              fontWeight: 600,
            }}>
              Production Shot Lists
            </span>
          </div>
          <div style={{ padding: '20px' }}>
            <p style={{
              fontSize: '0.65rem',
              color: '#444444',
              fontWeight: 600,
              margin: '0 0 4px',
            }}>
              SCENE {step.scenes.includes('B14') ? 'B14' : '14'} — {step.loc} — DAY
            </p>
            <p style={{
              fontSize: '0.8rem',
              color: '#888888',
              fontWeight: 700,
              margin: '0 0 16px',
            }}>
              Shot {step.scenes.includes('B14') ? 'B14C' : '14C'} — Medium Close-Up
            </p>

            {/* Shot details */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div>
                <p style={{
                  fontSize: '0.6rem',
                  color: '#61afef',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  margin: '0 0 4px',
                  opacity: 0.7,
                }}>
                  Subject
                </p>
                <div style={{
                  padding: '4px 6px',
                  margin: '0 -6px',
                  backgroundColor: 'rgba(42, 157, 143, 0.04)',
                  borderRadius: '4px',
                  border: '1px solid rgba(42, 157, 143, 0.1)',
                  transition: 'background-color 0.3s',
                }}>
                  <span style={{ ...valueStyle, fontSize: '0.8rem' }}>{renderField(step.role, 'role')} <span style={{ color: '#555555', fontWeight: 400 }}>({step.name})</span></span>
                </div>
              </div>
              <div>
                <p style={{
                  fontSize: '0.6rem',
                  color: '#61afef',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  margin: '0 0 4px',
                  opacity: 0.7,
                }}>
                  Lens
                </p>
                <span style={{ ...valueStyle, fontSize: '0.8rem', color: '#666666' }}>50mm</span>
              </div>
              <div>
                <p style={{
                  fontSize: '0.6rem',
                  color: '#61afef',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  margin: '0 0 4px',
                  opacity: 0.7,
                }}>
                  Movement
                </p>
                <span style={{ ...valueStyle, fontSize: '0.8rem', color: '#666666' }}>Static</span>
              </div>
            </div>
          </div>
        </div>
        {/* Shooting Schedules card */}
        <div style={{
          minWidth: 0,
          backgroundColor: '#111111',
          borderRadius: '16px',
          border: '1px solid #1a1a1a',
          overflow: 'hidden',
        }}>
          <div style={{
            padding: '10px 16px',
            borderBottom: '1px solid #1a1a1a',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}>
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: '#98c379',
            }} />
            <span style={{
              fontSize: '0.7rem',
              color: '#666666',
              fontWeight: 600,
            }}>
              Shooting Schedules
            </span>
          </div>
          <div style={{ padding: '0' }}>
            <p style={{
              fontSize: '0.65rem',
              color: '#444444',
              fontWeight: 600,
              margin: 0,
              padding: '12px 16px 8px',
            }}>
              DAY 3 — STRIPBOARD
            </p>

            {/* Strip rows */}
            {[
              { color: '#4a90d9', scene: 'Sc 1', desc: 'INT. APARTMENT', tod: 'NIGHT', cast: '1, 3', pages: '2 4/8', hasScene14: false },
              { color: '#ffffff', scene: step.scenes.includes('B14') ? 'Sc B14' : 'Sc 14', desc: step.loc, tod: 'DAY', cast: '1, 2, 5', pages: '3 2/8', hasScene14: true },
              { color: '#4aab5a', scene: 'Sc 7', desc: 'EXT. ROOFTOP', tod: 'NIGHT', cast: '1, 4', pages: '1 6/8', hasScene14: false },
            ].map((row, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  borderBottom: '1px solid #1a1a1a',
                  fontSize: '0.7rem',
                  position: 'relative',
                  backgroundColor: row.hasScene14 ? 'rgba(42, 157, 143, 0.04)' : `${row.color}08`,
                  border: row.hasScene14 ? '1px solid rgba(42, 157, 143, 0.1)' : 'none',
                  borderBottom: '1px solid #1a1a1a',
                }}
              >
                {/* Color strip */}
                <div style={{
                  width: '3px',
                  alignSelf: 'stretch',
                  backgroundColor: row.color,
                  opacity: 0.5,
                  flexShrink: 0,
                }} />
                <span style={{
                  color: row.hasScene14 ? '#cccccc' : '#888888',
                  fontWeight: 700,
                  padding: '8px 6px',
                  width: '42px',
                  flexShrink: 0,
                  fontSize: '0.65rem',
                }}>
                  {row.scene}
                </span>
                <span style={{
                  color: row.hasScene14 ? '#cccccc' : '#888888',
                  flex: 1,
                  padding: '8px 4px',
                  fontWeight: row.hasScene14 ? 700 : 500,
                  fontSize: '0.65rem',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                  {row.desc}
                </span>
                <span style={{
                  color: '#555555',
                  padding: '8px 6px',
                  fontSize: '0.6rem',
                  flexShrink: 0,
                }}>
                  {row.pages}
                </span>
              </div>
            ))}

            {/* Day break */}
            <div style={{
              padding: '4px 16px',
              backgroundColor: '#0d0d0d',
              borderBottom: '1px solid #1a1a1a',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <span style={{
                fontSize: '0.55rem',
                color: '#444444',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}>
                End of Day 3
              </span>
              <span style={{
                fontSize: '0.55rem',
                color: '#444444',
                fontWeight: 600,
              }}>
                7 2/8 pg
              </span>
            </div>
          </div>
        </div>
      </div>

      <ReplayButton onClick={() => { setStepIndex(0); setHasStarted(true); setIsVisible(true); }} visible={done} />
    </div>
  );
}

function CollabStripboard() {
  // TS edit state
  const [tsPhase, setTsPhase] = useState('idle');
  const [sc22Desc, setSc22Desc] = useState('EXT. STREET — DAY');
  const [tsPos, setTsPos] = useState({ x: 300, y: 12 });
  const tsTimerRef = useRef(null);
  const tsForward = useRef(true);

  // JM drag state
  const [jmPos, setJmPos] = useState({ x: 200, y: 15 });
  const [dragState, setDragState] = useState('none'); // 'none' | 'grabbed' | 'dragging'
  const [lunchOffset, setLunchOffset] = useState(0);
  const [sc7Offset, setSc7Offset] = useState(0);
  const jmTimerRef = useRef(null);
  const jmForward = useRef(true); // true = drag down, false = drag back up
  const [jmCycle, setJmCycle] = useState(0);
  const lunchRowRef = useRef(null);
  const sc7RowRef = useRef(null);

  // TS edit loop
  useEffect(() => {
    const to = tsForward.current ? 'PARK' : 'STREET';
    const steps = [
      { action: () => { setTsPos({ x: 300, y: 12 }); setTsPhase('idle'); }, delay: 3500 },
      { action: () => { setTsPos({ x: 160, y: 195 }); }, delay: 1200 },
      { action: () => { setTsPhase('editing'); }, delay: 400 },
      { action: () => { setSc22Desc('EXT.  — DAY'); }, delay: 300 },
      ...to.split('').map((_, i) => ({
        action: () => { setSc22Desc(`EXT. ${to.slice(0, i + 1)} — DAY`); },
        delay: 150 + Math.random() * 120,
      })),
      { action: () => {}, delay: 1800 },
      { action: () => { setTsPhase('idle'); setTsPos({ x: 380, y: 50 }); }, delay: 1200 },
      { action: () => { tsForward.current = !tsForward.current; }, delay: 5000 },
    ];

    let i = 0;
    const run = () => {
      if (i >= steps.length) { setTsPhase('restart'); return; }
      steps[i].action();
      tsTimerRef.current = setTimeout(() => { i++; run(); }, steps[i].delay);
    };
    run();
    return () => clearTimeout(tsTimerRef.current);
  }, [tsPhase === 'restart' ? Date.now() : null]);

  useEffect(() => {
    if (tsPhase === 'restart') setTsPhase('idle');
  }, [tsPhase]);

  // JM drag loop
  useEffect(() => {
    // Measure actual row heights and positions for pixel-perfect drag
    const lunchH = lunchRowRef.current?.offsetHeight || 28;
    const sc7H = sc7RowRef.current?.offsetHeight || 38;
    const lunchTop = lunchRowRef.current?.offsetTop || 76;
    // Cursor should be centered vertically on the banner
    const lunchCenterY = lunchTop + lunchH / 2 - 8; // -8 to align cursor tip
    const lunchY = jmForward.current ? lunchCenterY : lunchCenterY + sc7H;
    const dragTargetY = jmForward.current ? lunchCenterY + sc7H : lunchCenterY;

    const steps = [
      // Drift around
      { action: () => { setDragState('none'); setJmPos({ x: 100, y: 40 }); }, delay: 2500 },
      { action: () => { setJmPos({ x: 320, y: 130 }); }, delay: 2500 },
      { action: () => { setJmPos({ x: 180, y: 20 }); }, delay: 2000 },
      // Move to lunch banner
      { action: () => { setJmPos({ x: 240, y: lunchY }); }, delay: 1200 },
      // Grab
      { action: () => { setDragState('grabbed'); }, delay: 600 },
      // Drag — cursor moves, rows shift
      { action: () => {
        setDragState('dragging');
        setJmPos({ x: 240, y: dragTargetY });
        if (jmForward.current) {
          setLunchOffset(sc7H);
          setSc7Offset(-lunchH);
        } else {
          setLunchOffset(0);
          setSc7Offset(0);
        }
      }, delay: 900 },
      // Drop
      { action: () => {
        setDragState('none');
        jmForward.current = !jmForward.current;
      }, delay: 500 },
      // Drift away
      { action: () => { setJmPos({ x: 400, y: 15 }); }, delay: 4000 },
    ];

    let i = 0;
    const run = () => {
      if (i >= steps.length) { setJmCycle(c => c + 1); return; }
      steps[i].action();
      jmTimerRef.current = setTimeout(() => { i++; run(); }, steps[i].delay);
    };
    run();
    return () => clearTimeout(jmTimerRef.current);
  }, [jmCycle]);

  const isEditing = tsPhase === 'editing';
  const isGrabbed = dragState !== 'none';

  const rows = [
    { id: 'sc1', color: '#4a90d9', scene: 'Sc 1', desc: 'INT. APARTMENT — NIGHT', cast: '1, 3', pages: '2 4/8', status: null },
    { id: 'sc14', color: '#ffffff', scene: 'Sc 14', desc: 'INT. OFFICE — DAY', cast: '1, 2, 5', pages: '3 2/8', status: 'editing' },
    { id: 'lunch', type: 'banner', label: 'LUNCH — 12:00 PM to 1:00 PM' },
    { id: 'sc7', color: '#4aab5a', scene: 'Sc 7', desc: 'EXT. ROOFTOP — NIGHT', cast: '1, 4', pages: '1 6/8', status: null },
    { id: 'sc22', color: '#e8d44d', scene: 'Sc 22', desc: sc22Desc, cast: '2, 3', pages: '0 7/8', status: isEditing ? 'ts-editing' : null },
  ];

  const getRowTransform = (id) => {
    if (id === 'lunch') return `translateY(${lunchOffset}px)`;
    if (id === 'sc7') return `translateY(${sc7Offset}px)`;
    return undefined;
  };

  return (
    <div style={{ position: 'relative' }}>
      {/* KR drift cursor */}
      <CollabCursor label="KR" color="#61afef" startX={380} startY={110} bounds={{ minX: 80, maxX: 460, minY: 20, maxY: 190 }} initialDelay={2800} />

      {/* JM scripted cursor */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: 10,
        pointerEvents: 'none',
        willChange: 'transform',
        transform: `translate(${jmPos.x}px, ${jmPos.y}px)`,
        transition: 'transform 1s cubic-bezier(0.25, 0.1, 0.25, 1)',
      }}>
        <svg width="12" height="16" viewBox="0 0 12 16" fill="#e06c75">
          <path d="M0 0 L12 10 L5 10 L2 16 L0 0Z" />
        </svg>
        <span style={{
          fontSize: '0.5rem',
          backgroundColor: '#e06c75',
          color: '#ffffff',
          padding: '1px 5px',
          borderRadius: '3px',
          fontWeight: 600,
          marginLeft: '2px',
          whiteSpace: 'nowrap',
        }}>
          JM
        </span>
      </div>

      {/* TS scripted cursor */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: 10,
        pointerEvents: 'none',
        willChange: 'transform',
        transform: `translate(${tsPos.x}px, ${tsPos.y}px)`,
        transition: 'transform 1s cubic-bezier(0.25, 0.1, 0.25, 1)',
        opacity: isEditing ? 0 : 1,
      }}>
        <svg width="12" height="16" viewBox="0 0 12 16" fill="#98c379">
          <path d="M0 0 L12 10 L5 10 L2 16 L0 0Z" />
        </svg>
        <span style={{
          fontSize: '0.5rem',
          backgroundColor: '#98c379',
          color: '#ffffff',
          padding: '1px 5px',
          borderRadius: '3px',
          fontWeight: 600,
          marginLeft: '2px',
          whiteSpace: 'nowrap',
        }}>
          TS
        </span>
      </div>

      {/* Stripboard rows */}
      {rows.map((row) => (
        row.type === 'banner' ? (
          <div key={row.id} ref={lunchRowRef} style={{
            padding: '6px 20px',
            backgroundColor: isGrabbed ? '#1f1f0f' : '#1a1a0d',
            borderBottom: '1px solid #1a1a1a',
            textAlign: 'center',
            position: 'relative',
            zIndex: isGrabbed ? 5 : 1,
            transform: getRowTransform(row.id),
            transition: 'transform 0.7s cubic-bezier(0.25, 0.1, 0.25, 1), box-shadow 0.3s ease',
            boxShadow: isGrabbed ? '0 4px 16px rgba(0,0,0,0.5)' : 'none',
            border: isGrabbed ? '1px solid rgba(224, 108, 117, 0.25)' : '1px solid transparent',
          }}>
            <span style={{
              fontSize: '0.65rem',
              color: '#888844',
              fontWeight: 700,
              letterSpacing: '0.05em',
            }}>
              {row.label}
            </span>
          </div>
        ) :
        <div
          key={row.id}
          ref={row.id === 'sc7' ? sc7RowRef : undefined}
          style={{
            display: 'flex',
            alignItems: 'center',
            borderBottom: '1px solid #1a1a1a',
            fontSize: '0.75rem',
            position: 'relative',
            backgroundColor: `${row.color}0a`,
            transform: getRowTransform(row.id),
            transition: 'transform 0.7s cubic-bezier(0.25, 0.1, 0.25, 1)',
          }}
        >
          {/* Color strip */}
          <div style={{
            width: '4px',
            alignSelf: 'stretch',
            backgroundColor: row.color,
            opacity: 0.6,
            flexShrink: 0,
          }} />
          <span style={{
            color: '#888888',
            fontWeight: 700,
            padding: '10px 12px',
            width: '48px',
            flexShrink: 0,
          }}>
            {row.scene}
          </span>
          <span style={{
            color: '#cccccc',
            flex: 1,
            padding: '10px 8px',
            fontWeight: 700,
            whiteSpace: 'pre',
          }}>
            {row.status === 'ts-editing' ? (() => {
              const parts = row.desc.split(' — ');
              return <>{parts[0]}<span style={{
                display: 'inline-block',
                width: '2px',
                height: '1em',
                backgroundColor: '#98c379',
                verticalAlign: 'text-bottom',
                marginLeft: '-1px',
                marginRight: '-1px',
                animation: 'cursor-blink 0.8s step-end infinite',
              }} /> — {parts[1]}</>;
            })() : row.desc}
          </span>
          <span style={{
            color: '#555555',
            padding: '10px 12px',
            width: '60px',
            textAlign: 'right',
            flexShrink: 0,
          }}>
            {row.cast}
          </span>
          <span style={{
            color: '#555555',
            padding: '10px 12px',
            width: '50px',
            textAlign: 'right',
            flexShrink: 0,
          }}>
            {row.pages}
          </span>
          {/* KR active edit highlight */}
          {row.status === 'editing' && (
            <div style={{
              position: 'absolute',
              inset: 0,
              backgroundColor: 'rgba(97, 175, 239, 0.04)',
              border: '1px solid rgba(97, 175, 239, 0.15)',
              pointerEvents: 'none',
            }} />
          )}
          {/* TS active edit highlight */}
          {row.status === 'ts-editing' && (
            <div style={{
              position: 'absolute',
              inset: 0,
              backgroundColor: 'rgba(152, 195, 121, 0.04)',
              border: '1px solid rgba(152, 195, 121, 0.15)',
              pointerEvents: 'none',
              transition: 'opacity 0.3s ease',
            }} />
          )}
        </div>
      ))}

      {/* Day break */}
      <div style={{
        padding: '6px 20px',
        backgroundColor: '#0d0d0d',
        borderBottom: '1px solid #1a1a1a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <span style={{
          fontSize: '0.65rem',
          color: '#444444',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}>
          End of Day 1
        </span>
        <span style={{
          fontSize: '0.65rem',
          color: '#444444',
          fontWeight: 600,
        }}>
          8 2/8 pages
        </span>
      </div>
    </div>
  );
}

function CollabCursor({ label, color, startX, startY, bounds, initialDelay = 0 }) {
  const posRef = useRef({ x: startX, y: startY });
  const targetRef = useRef({ x: startX, y: startY });
  const startRef = useRef({ x: startX, y: startY });
  const waitRef = useRef(0);
  const elRef = useRef(null);
  const frameRef = useRef(null);

  useEffect(() => {
    const rand = Math.random;

    const pickTarget = () => {
      startRef.current = { x: posRef.current.x, y: posRef.current.y };
      targetRef.current = {
        x: bounds.minX + rand() * (bounds.maxX - bounds.minX),
        y: bounds.minY + rand() * (bounds.maxY - bounds.minY),
      };
      // Long pauses between moves — someone reading, thinking, then moving
      waitRef.current = 400 + rand() * 1440;
    };

    pickTarget();
    waitRef.current = initialDelay + waitRef.current;

    const tick = () => {
      if (waitRef.current > 0) {
        waitRef.current -= 16;
      } else {
        const dx = targetRef.current.x - posRef.current.x;
        const dy = targetRef.current.y - posRef.current.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < 0.5) {
          pickTarget();
        } else {
          // Smooth glide — slower when far away (ease in) and when close (ease out)
          const totalDist = Math.sqrt(
            Math.pow(targetRef.current.x - startRef.current.x, 2) +
            Math.pow(targetRef.current.y - startRef.current.y, 2)
          ) || 1;
          const progress = 1 - (dist / totalDist);
          // Bell curve: slow at start, gentle in middle, slow at end
          const t = 0.044 + 0.056 * Math.sin(progress * Math.PI);
          posRef.current.x += dx * t;
          posRef.current.y += dy * t;
        }
      }

      if (elRef.current) {
        elRef.current.style.transform = `translate(${posRef.current.x}px, ${posRef.current.y}px)`;
      }

      frameRef.current = requestAnimationFrame(tick);
    };

    frameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameRef.current);
  }, []);

  return (
    <div
      ref={elRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: 10,
        pointerEvents: 'none',
        willChange: 'transform',
        transform: `translate(${startX}px, ${startY}px)`,
      }}
    >
      <svg width="12" height="16" viewBox="0 0 12 16" fill={color}>
        <path d="M0 0 L12 10 L5 10 L2 16 L0 0Z" />
      </svg>
      <span style={{
        fontSize: '0.5rem',
        backgroundColor: color,
        color: '#ffffff',
        padding: '1px 5px',
        borderRadius: '3px',
        fontWeight: 600,
        marginLeft: '2px',
        whiteSpace: 'nowrap',
      }}>
        {label}
      </span>
    </div>
  );
}

export default function ProductionSuitePage() {
  const { projects, categories, loading } = useContext(ProjectsContext);

  if (loading) return <Loading />;

  const productionCategory = categories.find(c => c.name === 'Production');
  const productionProjects = productionCategory
    ? (projects.filter(p => p.development_category_id === productionCategory.id))
    : [];

  const apps = [
    {
      name: 'Script Breakdowns',
      tagline: 'Tag scene elements',
      description: 'Break down your script into actionable elements. Tag props, wardrobe, cast, vehicles, and everything your crew needs to prepare for each scene.',
    },
    {
      name: 'Production Shot Lists',
      tagline: 'Plan complex shots',
      description: 'Design every shot before you step on set. Organize by scene, assign shot types, and build a visual plan your entire team can follow.',
    },
    {
      name: 'Shooting Schedules',
      tagline: 'Scheduling with stripboards',
      description: 'Build your shooting schedule with the industry-standard stripboard. Drag and drop scenes, manage day breaks, and keep your production on track.',
    },
    {
      name: 'Cast & Crew Lists',
      tagline: 'Manage contacts & availability',
      description: 'Keep your entire cast and crew organized in one place. Track roles, contact details, and availability across your production.',
    },
    {
      name: 'Production Call Sheets',
      tagline: 'Prepare and distribute',
      description: 'Generate professional call sheets from your schedule. Include call times, locations, weather, and crew details — then share instantly.',
    },
    {
      name: 'Production Calendars',
      tagline: 'Track deadlines and tasks',
      description: 'See your entire production timeline at a glance. Track pre-production deadlines, shoot days, and post milestones in one unified calendar.',
    },
  ];

  // Match app metadata to Firestore projects
  const appsWithProjects = apps.map(app => {
    const project = productionProjects.find(p => p.name === app.name);
    return { ...app, project };
  });

  return (
    <FadeIn>
      {/* Hero */}
      <div style={{
        backgroundColor: '#000000',
        minHeight: 'calc(100vh - 80px)',
        padding: '120px 32px 80px',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        {/* Blue glow */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '120%',
          height: '300px',
          background: 'radial-gradient(ellipse at top center, rgba(255, 170, 50, 0.35) 0%, rgba(255, 100, 20, 0.15) 35%, rgba(140, 50, 180, 0.1) 60%, transparent 80%)',
          pointerEvents: 'none',
        }} />

        <div style={{
          position: 'relative',
          maxWidth: 'none',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '24px',
        }}>
          <h1 style={{
            fontSize: '3.5rem',
            fontWeight: 800,
            fontFamily: 'Helvetica, Arial, sans-serif',
            color: '#ffffff',
            margin: 0,
            letterSpacing: '-0.05em',
            lineHeight: 1.1,
          }}>
            {productionProjects.length} collaborative apps<br />for filmmaking.
          </h1>

          {/* App icons row */}
          <div style={{
            display: 'flex',
            gap: '24px',
            justifyContent: 'center',
            flexWrap: 'wrap',
            maxWidth: 'none',
            margin: '24px 0',
          }}>
            {productionProjects.filter(p => p.appIconURL).map((project) => (
              <Link
                key={project.id}
                to={`/${getProjectSlug(project)}`}
                style={{
                  width: '120px',
                  height: '120px',
                  position: 'relative',
                  flexShrink: 0,
                  display: 'block',
                  transition: 'transform 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.08)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                {/* Border layer */}
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  clipPath: 'url(#squircle-clip)',
                  backgroundColor: '#666666',
                }} />
                {/* Content layer */}
                <div style={{
                  position: 'absolute',
                  inset: '1px',
                  clipPath: 'url(#squircle-clip)',
                  overflow: 'hidden',
                  backgroundColor: '#1a1a1a',
                }}>
                  <img
                    src={project.appIconURL}
                    alt={project.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                      opacity: 0,
                      transition: 'opacity 0.3s ease',
                    }}
                    onLoad={(e) => { e.currentTarget.style.opacity = '1'; }}
                  />
                </div>
              </Link>
            ))}
          </div>

          <p style={{
            fontSize: '1rem',
            color: '#999999',
            margin: 0,
            maxWidth: '540px',
            lineHeight: 1.6,
            fontWeight: 500,
          }}>
            <span style={{ color: '#ffffff', fontWeight: 700 }}>Every tool your production needs. </span>
            Break down scripts. Plan shots. Build stripboard schedules. Manage cast and crew. Generate call sheets. Schedule events and assign tasks. All designed to work together, in real time, and even offline.
          </p>

          <p style={{
            fontSize: '0.9rem',
            color: '#666666',
            margin: '8px 0 0',
            fontWeight: 600,
          }}>
            Free on iPhone, iPad, and Mac.
          </p>
        </div>
      </div>


      {/* Update Once banner */}
      <div style={{
        backgroundColor: '#000000',
        padding: '120px 32px',
        position: 'relative',
        overflow: 'hidden',
        borderTop: '1px solid #1a1a1a',
      }}>
        {/* Warm amber/purple glow */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '120%',
          height: '300px',
          background: 'radial-gradient(ellipse at top center, rgba(200, 120, 50, 0.07) 0%, rgba(140, 50, 180, 0.04) 50%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'relative',
          maxWidth: 'none',
          margin: '0 auto',
          textAlign: 'center',
        }}>
          <h2 style={{
            fontSize: '3.5rem',
            fontWeight: 800,
            fontFamily: 'Helvetica, Arial, sans-serif',
            color: '#ffffff',
            margin: '0 0 16px',
            letterSpacing: '-0.05em',
            lineHeight: 1.1,
          }}>
            Update once.<br />Updates everywhere.
          </h2>
          <p style={{
            fontSize: '1rem',
            color: '#999999',
            margin: '0 auto 48px',
            lineHeight: 1.6,
            maxWidth: '460px',
          }}>
            Rename a character, reassign a role, renumber a scene, change a location — and every app updates instantly. Like magic.
          </p>

          <UpdateOnceDemo />
        </div>
      </div>

      {/* Collaboration banner */}
      <div style={{
        backgroundColor: '#0a0a0a',
        padding: '120px 32px',
        position: 'relative',
        overflow: 'hidden',
        borderTop: '1px solid #1a1a1a',
      }}>
        {/* Blue/indigo glow from top */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '120%',
          height: '300px',
          background: 'radial-gradient(ellipse at top center, rgba(60, 80, 180, 0.08) 0%, rgba(60, 80, 180, 0.03) 40%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'relative',
          maxWidth: '700px',
          margin: '0 auto',
          textAlign: 'center',
        }}>
          <h2 style={{
            fontSize: '3.5rem',
            fontWeight: 800,
            fontFamily: 'Helvetica, Arial, sans-serif',
            color: '#ffffff',
            margin: '0 0 16px',
            letterSpacing: '-0.05em',
            lineHeight: 1.1,
          }}>
            Work together.<br />In real time.
          </h2>
          <p style={{
            fontSize: '1rem',
            color: '#999999',
            margin: '0 auto 48px',
            lineHeight: 1.6,
            maxWidth: '460px',
          }}>
            Your AD updates the schedule. Your producer sees it live.
            Everyone stays on the same page, on set or off.
          </p>

          {/* Simulated collaborative UI */}
          <style>{`
            @keyframes presence-fade {
              0%, 100% { opacity: 0.7; }
              50% { opacity: 1; }
            }
          `}</style>
          <div style={{
            backgroundColor: '#111111',
            borderRadius: '16px',
            border: '1px solid #1a1a1a',
            padding: '0',
            overflow: 'hidden',
            textAlign: 'left',
            maxWidth: '560px',
            margin: '0 auto',
          }}>
            {/* Title bar */}
            <div style={{
              padding: '12px 20px',
              borderBottom: '1px solid #1a1a1a',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <span style={{
                fontSize: '0.8rem',
                color: '#666666',
                fontWeight: 600,
              }}>
                Shooting Schedule — "Midnight in Brooklyn"
              </span>
              {/* Active users */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0px',
              }}>
                {[
                  { initials: 'JM', color: '#e06c75', role: 'Director' },
                  { initials: 'KR', color: '#61afef', role: '1st AD' },
                  { initials: 'TS', color: '#98c379', role: 'Producer' },
                ].map((user, i) => (
                  <div
                    key={user.initials}
                    title={`${user.role}`}
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      backgroundColor: user.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.55rem',
                      fontWeight: 700,
                      color: '#ffffff',
                      border: '2px solid #111111',
                      marginLeft: i > 0 ? '-8px' : '0',
                      zIndex: 3 - i,
                      position: 'relative',
                      animation: 'presence-fade 3s ease infinite',
                      animationDelay: `${i * 0.5}s`,
                    }}
                  >
                    {user.initials}
                  </div>
                ))}
                {/* <span style={{
                  fontSize: '0.65rem',
                  color: '#555555',
                  marginLeft: '8px',
                  fontWeight: 600,
                }}>
                  3 active
                </span> */}
              </div>
            </div>

            <CollabStripboard />
          </div>
        </div>
      </div>

      {/* "Better than a spreadsheet" banner */}
      <div style={{
        backgroundColor: '#0a0a0a',
        padding: '120px 32px',
        position: 'relative',
        overflow: 'hidden',
        borderTop: '1px solid #1a1a1a',
      }}>
        {/* Faux spreadsheet grid background */}
        <div style={{
          position: 'absolute',
          inset: 0,
          opacity: 0.07,
          backgroundImage: `
            linear-gradient(to right, #ffffff 1px, transparent 1px),
            linear-gradient(to bottom, #ffffff 1px, transparent 1px)
          `,
          backgroundSize: '80px 32px',
        }} />
        {/* Fade overlay — grid fades out toward center */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(ellipse at center, #0a0a0a 30%, transparent 80%)',
        }} />

        <div style={{
          position: 'relative',
          maxWidth: '700px',
          margin: '0 auto',
          textAlign: 'center',
        }}>
          <h2 style={{
            fontSize: '3.5rem',
            fontWeight: 800,
            fontFamily: 'Helvetica, Arial, sans-serif',
            color: '#ffffff',
            margin: '0 0 24px',
            letterSpacing: '-0.05em',
            lineHeight: 1.1,
          }}>
            Better than spreadsheets.
          </h2>
          <p style={{
            fontSize: '1rem',
            color: '#999999',
            margin: 0,
            lineHeight: 1.6,
            maxWidth: '480px',
            marginLeft: 'auto',
            marginRight: 'auto',
          }}>
            Real stripboards, real call sheets, real breakdowns.<br />No formulas, no formatting, no pinching to zoom on your phone.
          </p>
        </div>
      </div>

      {/* Work from anywhere banner */}
      <div style={{
        backgroundColor: '#000000',
        padding: '120px 32px',
        position: 'relative',
        overflow: 'hidden',
        borderTop: '1px solid #1a1a1a',
      }}>
        {/* Teal/cyan glow */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '120%',
          height: '300px',
          background: 'radial-gradient(ellipse at top center, rgba(42, 157, 143, 0.08) 0%, rgba(42, 157, 143, 0.03) 40%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        <div style={{
          maxWidth: 'none',
          margin: '0 auto',
          textAlign: 'center',
        }}>
          <h2 style={{
            fontSize: '3.5rem',
            fontWeight: 800,
            fontFamily: 'Helvetica, Arial, sans-serif',
            color: '#ffffff',
            margin: '0 0 16px',
            letterSpacing: '-0.05em',
            lineHeight: 1.1,
          }}>
            On set with your iPhone.<br />In the office on your Mac.
          </h2>
          <p style={{
            fontSize: '1rem',
            color: '#999999',
            margin: '0 auto 64px',
            lineHeight: 1.6,
            maxWidth: '460px',
          }}>
            iPhone, iPad, and Mac. Works offline, syncs when you're back. Your production goes where you go.
          </p>

          {/* Device mockups — fixed sizes in a scrollable container on small screens */}
          <div style={{
            overflowX: 'auto',
            overflowY: 'visible',
            WebkitOverflowScrolling: 'touch',
            padding: '0 16px',
          }}>
          <div style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            gap: '30px',
            minWidth: '1100px',
          }}>
            {/* iPhone */}
            <div style={{
              width: '75px',
              aspectRatio: '9 / 19.5',
              borderRadius: '16px',
              border: '2px solid #333333',
              backgroundColor: '#0a0a0a',
              position: 'relative',
              overflow: 'hidden',
              flexShrink: 0,
            }}>
              {/* Screen content — mini stripboard */}
              <div style={{ padding: '20px 6px 6px' }}>
                {[
                  { color: '#4a90d9', w: '100%' },
                  { color: '#ffffff', w: '100%' },
                  { color: '#4aab5a', w: '100%' },
                  { color: '#e8d44d', w: '100%' },
                ].map((row, i) => (
                  <div key={i} style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '3px',
                    height: '12px',
                    backgroundColor: `${row.color}08`,
                    borderRadius: '2px',
                  }}>
                    <div style={{
                      width: '2px',
                      height: '100%',
                      backgroundColor: row.color,
                      opacity: 0.4,
                      borderRadius: '1px',
                      flexShrink: 0,
                    }} />
                    <div style={{
                      height: '4px',
                      width: '30%',
                      backgroundColor: '#333333',
                      borderRadius: '2px',
                      marginLeft: '4px',
                    }} />
                    <div style={{
                      height: '4px',
                      flex: 1,
                      backgroundColor: '#222222',
                      borderRadius: '2px',
                      marginLeft: '3px',
                    }} />
                  </div>
                ))}
                <div style={{
                  height: '8px',
                  backgroundColor: '#111111',
                  borderRadius: '2px',
                  marginTop: '3px',
                }} />
              </div>
            </div>

            {/* iPad */}
            <div style={{
              width: '190px',
              minWidth: '190px',
              aspectRatio: '3 / 4',
              borderRadius: '16px',
              border: '2px solid #333333',
              backgroundColor: '#0a0a0a',
              position: 'relative',
              overflow: 'hidden',
            }}>
              {/* Screen content */}
              <div style={{ padding: '10px 6px 6px' }}>
                {/* Toolbar */}
                <div style={{
                  display: 'flex',
                  gap: '4px',
                  marginBottom: '8px',
                }}>
                  <div style={{ height: '6px', width: '40px', backgroundColor: '#222222', borderRadius: '3px' }} />
                  <div style={{ flex: 1 }} />
                  <div style={{ height: '6px', width: '20px', backgroundColor: '#222222', borderRadius: '3px' }} />
                </div>
                {[
                  { color: '#4a90d9' },
                  { color: '#ffffff' },
                  { color: '#4aab5a' },
                  { color: '#e8d44d' },
                  { color: '#4a90d9' },
                  { color: '#ffffff' },
                ].map((row, i) => (
                  <div key={i} style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '4px',
                    height: '16px',
                    backgroundColor: `${row.color}08`,
                    borderRadius: '3px',
                  }}>
                    <div style={{
                      width: '2px',
                      height: '100%',
                      backgroundColor: row.color,
                      opacity: 0.4,
                      borderRadius: '1px',
                      flexShrink: 0,
                    }} />
                    <div style={{
                      height: '5px',
                      width: '25%',
                      backgroundColor: '#333333',
                      borderRadius: '2px',
                      marginLeft: '6px',
                    }} />
                    <div style={{
                      height: '5px',
                      width: '35%',
                      backgroundColor: '#222222',
                      borderRadius: '2px',
                      marginLeft: '4px',
                    }} />
                    <div style={{ flex: 1 }} />
                    <div style={{
                      height: '5px',
                      width: '15%',
                      backgroundColor: '#1a1a1a',
                      borderRadius: '2px',
                      marginRight: '6px',
                    }} />
                  </div>
                ))}
                <div style={{
                  height: '10px',
                  backgroundColor: '#111111',
                  borderRadius: '3px',
                  marginTop: '4px',
                }} />
              </div>
            </div>

            {/* MacBook */}
            <div style={{
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}>
              {/* Screen */}
              <div style={{
                width: '320px',
                aspectRatio: '16 / 10',
                borderRadius: '12px 12px 0 0',
                border: '2px solid #333333',
                borderBottom: 'none',
                backgroundColor: '#0a0a0a',
                position: 'relative',
                overflow: 'hidden',
              }}>
                {/* Screen content */}
                <div style={{ padding: '10px 6px 6px' }}>
                  {/* Toolbar */}
                  <div style={{
                    display: 'flex',
                    gap: '4px',
                    marginBottom: '8px',
                    alignItems: 'center',
                  }}>
                    {/* Traffic lights */}
                    <div style={{ display: 'flex', gap: '3px', marginRight: '8px' }}>
                      <div style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: '#ff5f57' }} />
                      <div style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: '#febc2e' }} />
                      <div style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: '#28c840' }} />
                    </div>
                    <div style={{ height: '6px', width: '60px', backgroundColor: '#222222', borderRadius: '3px' }} />
                    <div style={{ flex: 1 }} />
                    <div style={{ height: '6px', width: '30px', backgroundColor: '#222222', borderRadius: '3px' }} />
                    <div style={{ height: '6px', width: '20px', backgroundColor: '#222222', borderRadius: '3px' }} />
                  </div>
                  {/* Sidebar + Content */}
                  <div style={{ display: 'flex', gap: '6px' }}>
                    {/* Sidebar */}
                    <div style={{ width: '50px', flexShrink: 0 }}>
                      {[1,2,3,4,5].map(i => (
                        <div key={i} style={{
                          height: '5px',
                          backgroundColor: i === 2 ? '#333333' : '#1a1a1a',
                          borderRadius: '2px',
                          marginBottom: '4px',
                          width: i === 2 ? '100%' : '80%',
                        }} />
                      ))}
                    </div>
                    {/* Main content — stripboard */}
                    <div style={{ flex: 1 }}>
                      {[
                        { color: '#4a90d9' },
                        { color: '#ffffff' },
                        { color: '#4aab5a' },
                        { color: '#e8d44d' },
                        { color: '#4a90d9' },
                        { color: '#ffffff' },
                        { color: '#4aab5a' },
                      ].map((row, i) => (
                        <div key={i} style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginBottom: '3px',
                          height: '14px',
                          backgroundColor: `${row.color}08`,
                          borderRadius: '2px',
                        }}>
                          <div style={{
                            width: '2px',
                            height: '100%',
                            backgroundColor: row.color,
                            opacity: 0.4,
                            borderRadius: '1px',
                            flexShrink: 0,
                          }} />
                          <div style={{
                            height: '4px',
                            width: '18%',
                            backgroundColor: '#333333',
                            borderRadius: '2px',
                            marginLeft: '5px',
                          }} />
                          <div style={{
                            height: '4px',
                            width: '30%',
                            backgroundColor: '#222222',
                            borderRadius: '2px',
                            marginLeft: '4px',
                          }} />
                          <div style={{ flex: 1 }} />
                          <div style={{
                            height: '4px',
                            width: '10%',
                            backgroundColor: '#1a1a1a',
                            borderRadius: '2px',
                            marginRight: '5px',
                          }} />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              {/* Base/hinge */}
              <div style={{
                width: '340px',
                height: '8px',
                backgroundColor: '#222222',
                borderRadius: '0 0 4px 4px',
                borderTop: '1px solid #444444',
              }} />
            </div>

            {/* iMac 24" */}
            <div style={{
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}>
              {/* Display */}
              <div style={{
                width: '500px',
                height: '310px',
                borderRadius: '12px 12px 0 0',
                border: '2px solid #333333',
                borderBottom: 'none',
                backgroundColor: '#0a0a0a',
                position: 'relative',
                overflow: 'hidden',
              }}>
                {/* Screen content */}
                <div style={{ padding: '20px 16px 16px' }}>
                  {/* Toolbar */}
                  <div style={{
                    display: 'flex',
                    gap: '4px',
                    marginBottom: '6px',
                    alignItems: 'center',
                  }}>
                    {/* Traffic lights */}
                    <div style={{ display: 'flex', gap: '3px', marginRight: '8px' }}>
                      <div style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: '#ff5f57' }} />
                      <div style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: '#febc2e' }} />
                      <div style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: '#28c840' }} />
                    </div>
                    <div style={{ height: '5px', width: '60px', backgroundColor: '#222222', borderRadius: '3px' }} />
                    <div style={{ flex: 1 }} />
                    <div style={{ height: '5px', width: '30px', backgroundColor: '#222222', borderRadius: '3px' }} />
                    <div style={{ height: '5px', width: '20px', backgroundColor: '#222222', borderRadius: '3px' }} />
                  </div>
                  {/* Sidebar + Content */}
                  <div style={{ display: 'flex', gap: '6px' }}>
                    {/* Sidebar */}
                    <div style={{ width: '60px', flexShrink: 0 }}>
                      {[1,2,3,4,5,6].map(i => (
                        <div key={i} style={{
                          height: '5px',
                          backgroundColor: i === 2 ? '#333333' : '#1a1a1a',
                          borderRadius: '2px',
                          marginBottom: '4px',
                          width: i === 2 ? '100%' : '80%',
                        }} />
                      ))}
                    </div>
                    {/* Main content — stripboard */}
                    <div style={{ flex: 1 }}>
                      {[
                        { color: '#4a90d9' },
                        { color: '#ffffff' },
                        { color: '#4aab5a' },
                        { color: '#e8d44d' },
                        { color: '#4a90d9' },
                        { color: '#ffffff' },
                        { color: '#4aab5a' },
                        { color: '#e8d44d' },
                        { color: '#4a90d9' },
                      ].map((row, i) => (
                        <div key={i} style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginBottom: '3px',
                          height: '14px',
                          backgroundColor: `${row.color}08`,
                          borderRadius: '2px',
                        }}>
                          <div style={{
                            width: '2px',
                            height: '100%',
                            backgroundColor: row.color,
                            opacity: 0.4,
                            borderRadius: '1px',
                            flexShrink: 0,
                          }} />
                          <div style={{
                            height: '4px',
                            width: '14%',
                            backgroundColor: '#333333',
                            borderRadius: '2px',
                            marginLeft: '5px',
                          }} />
                          <div style={{
                            height: '4px',
                            width: '25%',
                            backgroundColor: '#222222',
                            borderRadius: '2px',
                            marginLeft: '4px',
                          }} />
                          <div style={{ flex: 1 }} />
                          <div style={{
                            height: '4px',
                            width: '8%',
                            backgroundColor: '#1a1a1a',
                            borderRadius: '2px',
                            marginRight: '5px',
                          }} />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              {/* Chin */}
              <div style={{
                width: '500px',
                height: '56px',
                background: 'linear-gradient(180deg, #2a2a2a 0%, #1a1a1a 100%)',
                border: '2px solid #333333',
                borderTop: 'none',
                borderRadius: '0 0 12px 12px',
              }} />
              {/* Stand neck */}
              <div style={{
                width: '100px',
                height: '50px',
                background: 'linear-gradient(180deg, #2a2a2a 0%, #1a1a1a 100%)',
                borderLeft: '1px solid #333333',
                borderRight: '1px solid #333333',
              }} />
              {/* Stand base */}
              <div style={{
                width: '112px',
                height: '8px',
                background: 'linear-gradient(180deg, #333333 0%, #222222 100%)',
                borderRadius: '2px',
                border: '1px solid #3a3a3a',
              }} />
            </div>
          </div>
          </div>
        </div>
      </div>

      {/* "Free" banner — targeting indie/student filmmakers */}
      <div style={{
        backgroundColor: '#000000',
        padding: '140px 32px',
        position: 'relative',
        overflow: 'hidden',
        borderTop: '1px solid #1a1a1a',
      }}>
        {/* Film strip perforations — left */}
        <div style={{
          position: 'absolute',
          left: '24px',
          top: 0,
          bottom: 0,
          width: '16px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: '12px',
          opacity: 0.08,
          pointerEvents: 'none',
        }}>
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} style={{
              width: '16px',
              height: '10px',
              borderRadius: '3px',
              backgroundColor: '#ffffff',
            }} />
          ))}
        </div>
        {/* Film strip perforations — right */}
        <div style={{
          position: 'absolute',
          right: '24px',
          top: 0,
          bottom: 0,
          width: '16px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: '12px',
          opacity: 0.08,
          pointerEvents: 'none',
        }}>
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} style={{
              width: '16px',
              height: '10px',
              borderRadius: '3px',
              backgroundColor: '#ffffff',
            }} />
          ))}
        </div>

        <div style={{
          position: 'relative',
          maxWidth: '600px',
          margin: '0 auto',
          textAlign: 'center',
        }}>
          <h2 style={{
            fontSize: '2rem',
            fontWeight: 800,
            fontFamily: 'Helvetica, Arial, sans-serif',
            color: '#999999',
            margin: '0 0 8px',
            letterSpacing: '-0.04em',
          }}>
            Professional tools.
          </h2>
          <h2 style={{
            fontSize: '6rem',
            fontWeight: 900,
            fontFamily: 'Helvetica, Arial, sans-serif',
            background: 'linear-gradient(180deg, #ffffff 30%, #666666 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            margin: '0 0 32px',
            letterSpacing: '-0.05em',
            lineHeight: 1,
          }}>
            Free.
          </h2>
          <p style={{
            fontSize: '1rem',
            color: '#999999',
            margin: 0,
            lineHeight: 1.8,
            maxWidth: '460px',
            marginLeft: 'auto',
            marginRight: 'auto',
          }}>
            Student film. Indie short. First feature.
            Budget shouldn't decide whether your production
            runs on professional tools or a spreadsheet.
          </p>
        </div>
      </div>

      {/* Temporarily hidden — all sections below until individual app sections */}
      {false && (<>
      {/* "Better Together" section */}
      <div style={{
        backgroundColor: '#000000',
        padding: '80px 32px',
        borderTop: '1px solid #1a1a1a',
      }}>
        <div style={{
          maxWidth: '800px',
          margin: '0 auto',
          textAlign: 'center',
        }}>
          <h2 style={{
            fontSize: '2.5rem',
            fontWeight: 800,
            fontFamily: 'Helvetica, Arial, sans-serif',
            color: '#ffffff',
            margin: '0 0 16px',
            letterSpacing: '-0.05em',
          }}>
            Great alone. Better together.
          </h2>
          <p style={{
            fontSize: '1.1rem',
            color: '#999999',
            margin: '0 auto 64px',
            maxWidth: '560px',
            lineHeight: 1.6,
          }}>
            Break down your script, and your elements flow into shot lists.
            Shot lists feed into schedules. Schedules generate call sheets.
            Cast & crew stay in sync across every app. Calendars tie it all together.
          </p>

          <FlowDiagram />
        </div>
      </div>

      {/* Live sync demo — name change propagates everywhere */}
      <div style={{
        backgroundColor: '#0a0a0a',
        padding: '100px 32px',
        borderTop: '1px solid #1a1a1a',
      }}>
        <div style={{
          maxWidth: '800px',
          margin: '0 auto',
          textAlign: 'center',
        }}>
          <h2 style={{
            fontSize: '2.5rem',
            fontWeight: 800,
            fontFamily: 'Helvetica, Arial, sans-serif',
            color: '#ffffff',
            margin: '0 0 16px',
            letterSpacing: '-0.05em',
          }}>
            Change it once. Updated everywhere.
          </h2>
          <p style={{
            fontSize: '1.1rem',
            color: '#999999',
            margin: '0 auto 64px',
            maxWidth: '560px',
            lineHeight: 1.6,
          }}>
            Edit a cast member, rename a scene, adjust a schedule —
            every app sees the change instantly.
          </p>

          <SyncDemo productionProjects={productionProjects} />
        </div>
      </div>

      {/* Free & Accessible */}
      <div style={{
        backgroundColor: '#000000',
        padding: '100px 32px',
        borderTop: '1px solid #1a1a1a',
        textAlign: 'center',
      }}>
        <div style={{
          maxWidth: '600px',
          margin: '0 auto',
        }}>
          <h2 style={{
            fontSize: '3rem',
            fontWeight: 800,
            fontFamily: 'Helvetica, Arial, sans-serif',
            color: '#ffffff',
            margin: '0 0 16px',
            letterSpacing: '-0.05em',
          }}>
            Free. All of it.
          </h2>
          <p style={{
            fontSize: '1.1rem',
            color: '#999999',
            margin: 0,
            lineHeight: 1.6,
          }}>
            No subscriptions. No paywalls. No feature gates.
            Every app, every feature, on iPhone, iPad, and Mac.
          </p>
        </div>
      </div>

      {/* Collaborative */}
      <div style={{
        backgroundColor: '#0a0a0a',
        padding: '100px 32px',
        borderTop: '1px solid #1a1a1a',
        textAlign: 'center',
      }}>
        <div style={{
          maxWidth: '600px',
          margin: '0 auto',
        }}>
          <h2 style={{
            fontSize: '3rem',
            fontWeight: 800,
            fontFamily: 'Helvetica, Arial, sans-serif',
            color: '#ffffff',
            margin: '0 0 16px',
            letterSpacing: '-0.05em',
          }}>
            Share a production. Work together live.
          </h2>
          <p style={{
            fontSize: '1.1rem',
            color: '#999999',
            margin: 0,
            lineHeight: 1.6,
          }}>
            Invite your crew and collaborate in real time.
            Everyone works on the same production, always in sync.
          </p>
        </div>
      </div>

      </>)}

      {/* Individual app sections — temporarily commented out */}
      {false && appsWithProjects.map((app, index) => (
        <div
          key={app.name}
          style={{
            backgroundColor: index % 2 === 0 ? '#000000' : '#0a0a0a',
            padding: '100px 32px',
            borderTop: '1px solid #1a1a1a',
          }}
        >
          <div style={{
            maxWidth: '900px',
            margin: '0 auto',
            display: 'flex',
            flexDirection: index % 2 === 0 ? 'row' : 'row-reverse',
            alignItems: 'center',
            gap: '64px',
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}>
            {/* App icon */}
            <div style={{
              width: '160px',
              height: '160px',
              flexShrink: 0,
              position: 'relative',
            }}>
              <div style={{
                position: 'absolute',
                inset: 0,
                clipPath: 'url(#squircle-clip)',
                backgroundColor: '#666666',
              }} />
              <div style={{
                position: 'absolute',
                inset: '1px',
                clipPath: 'url(#squircle-clip)',
                overflow: 'hidden',
                backgroundColor: '#1a1a1a',
              }}>
                {app.project?.appIconURL ? (
                  <img
                    src={app.project.appIconURL}
                    alt={app.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                      opacity: 0,
                      transition: 'opacity 0.3s ease',
                    }}
                    onLoad={(e) => { e.currentTarget.style.opacity = '1'; }}
                  />
                ) : (
                  <div style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '3rem',
                    fontWeight: 900,
                    fontFamily: 'Helvetica, Arial, sans-serif',
                    color: '#333333',
                  }}>
                    {app.name.charAt(0)}
                  </div>
                )}
              </div>
            </div>

            {/* App info */}
            <div style={{
              flex: 1,
              minWidth: '280px',
              textAlign: 'left',
            }}>
              <h3 style={{
                fontSize: '2rem',
                fontWeight: 800,
                fontFamily: 'Helvetica, Arial, sans-serif',
                color: '#ffffff',
                margin: '0 0 4px',
                letterSpacing: '-0.04em',
              }}>
                {app.name}
              </h3>
              <p style={{
                fontSize: '0.9rem',
                color: '#666666',
                margin: '0 0 16px',
                fontWeight: 600,
              }}>
                {app.tagline}
              </p>
              <p style={{
                fontSize: '1.05rem',
                color: '#999999',
                margin: 0,
                lineHeight: 1.6,
              }}>
                {app.description}
              </p>
              {app.project && (
                <Link
                  to={`/${getProjectSlug(app.project)}`}
                  style={{
                    display: 'inline-block',
                    marginTop: '20px',
                    padding: '10px 24px',
                    backgroundColor: '#1a1a1a',
                    color: '#ffffff',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    fontFamily: 'Helvetica, Arial, sans-serif',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    border: '1px solid #333333',
                    transition: 'background-color 0.2s ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#222222'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#1a1a1a'; }}
                >
                  Learn more
                </Link>
              )}
            </div>
          </div>
        </div>
      ))}

      {/* Bottom CTA — temporarily hidden */}
      {false && (
      <div style={{
        backgroundColor: '#000000',
        padding: '100px 32px',
        textAlign: 'center',
        borderTop: '1px solid #1a1a1a',
      }}>
        <div style={{
          maxWidth: '600px',
          margin: '0 auto',
        }}>
          <h2 style={{
            fontSize: '2.5rem',
            fontWeight: 800,
            fontFamily: 'Helvetica, Arial, sans-serif',
            color: '#ffffff',
            margin: '0 0 16px',
            letterSpacing: '-0.05em',
          }}>
            Your entire production. One ecosystem.
          </h2>
          <p style={{
            fontSize: '1.1rem',
            color: '#999999',
            margin: '0 0 32px',
            lineHeight: 1.6,
          }}>
            Available for free on iPhone, iPad, and Mac.
          </p>
        </div>
      </div>
      )}
    </FadeIn>
  );
}
